import { SESSION_EXPIRED_ERROR_CODE } from "@repo/app-types/api/user";
import { z } from "zod";
import { handleServiceAccessRequiredResponse } from "../features/serviceAccess/serviceAccessClient.js";
import { checkIsServiceMode } from "../features/serviceAccess/serviceAccessMode.js";
import { requiresPopupAuth } from "./popupAuth.js";
import { startSsoSignInWithProviderId } from "./signInActions.js";
import { getSupabaseClient } from "./supabaseClient.js";

/**
 * The body of the 401 a protected route answers for a session older than the
 * company's maximum session age.
 */
const sessionExpiredErrorBodySchema = z.object({
  code: z.literal(SESSION_EXPIRED_ERROR_CODE),
  sso_provider_ids: z.array(z.string()),
});

/**
 * When the session last sent the user to their SSO provider for an expired
 * session. A second expiry within the cooldown lands on the sign-in page
 * instead, so a provider whose sessions never pass the check can't loop.
 */
const SSO_RESTART_STORAGE_KEY = "alpharun.sessionExpiredSsoRestartAt";
const SSO_RESTART_COOLDOWN_MS = 60 * 1000;

/**
 * How a restart ended: the page is leaving for sign-in, or the sign-out failed
 * and the session is still in place, so the user can retry.
 */
export type SessionRestartOutcome = "redirecting" | "failed";

/** The current restart, shared by every caller on the page. */
let restartInProgress: Promise<SessionRestartOutcome> | null = null;

/**
 * The company's SSO provider ids when an API error response says the session
 * expired, else null.
 */
function readSessionExpiredSsoProviderIds(
  httpStatus: unknown,
  body: unknown,
): string[] | null {
  if (httpStatus !== 401) {
    return null;
  }
  const result = sessionExpiredErrorBodySchema.safeParse(body);
  return result.success ? result.data.sso_provider_ids : null;
}

function checkSsoRestartedRecently(): boolean {
  try {
    const restartedAt = Number(
      window.sessionStorage.getItem(SSO_RESTART_STORAGE_KEY),
    );
    return Date.now() - restartedAt < SSO_RESTART_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function recordSsoRestart(): void {
  try {
    window.sessionStorage.setItem(SSO_RESTART_STORAGE_KEY, String(Date.now()));
  } catch {
    // Without storage the loop guard is off; the restart still works.
  }
}

/**
 * Ends an expired session and starts sign-in again: straight at the company's
 * SSO provider when it has one, else on the sign-in page. Refreshing the token
 * can't help, because a refresh keeps the original sign-in time. Runs once per
 * page, however many requests report the expiry, unless it fails; a later call
 * then tries again.
 */
export function restartExpiredSessionSignIn(
  ssoProviderIds: readonly string[],
): Promise<SessionRestartOutcome> {
  restartInProgress ??= restartSignInOnce(ssoProviderIds);
  return restartInProgress;
}

async function restartSignInOnce(
  ssoProviderIds: readonly string[],
): Promise<SessionRestartOutcome> {
  let outcome: SessionRestartOutcome;
  try {
    outcome = await signOutAndRestartSignIn(ssoProviderIds);
  } catch {
    outcome = "failed";
  }
  if (outcome === "failed") {
    restartInProgress = null;
  }
  return outcome;
}

async function signOutAndRestartSignIn(
  ssoProviderIds: readonly string[],
): Promise<SessionRestartOutcome> {
  // A failed local sign-out can leave the session in storage, and reloading
  // with it would only report the expiry again.
  const { error: signOutError } = await getSupabaseClient().auth.signOut({
    scope: "local",
  });
  if (signOutError) {
    return "failed";
  }

  // A popup can't open without a click, so embedded sessions sign in from the
  // sign-in page.
  const ssoProviderId = ssoProviderIds[0];
  if (ssoProviderId && !requiresPopupAuth() && !checkSsoRestartedRecently()) {
    recordSsoRestart();
    const { error } = await startSsoSignInWithProviderId(ssoProviderId, {
      usePopup: false,
    });
    if (!error) {
      return "redirecting";
    }
  }

  // Signed out, the app renders the sign-in page at the current URL.
  window.location.reload();
  return "redirecting";
}

/**
 * Starts {@link restartExpiredSessionSignIn} when an API error response says
 * the session expired, or the service access sign-in when it says the service
 * access token no longer resolves.
 */
export function handleSessionExpiredResponse(
  httpStatus: unknown,
  body: unknown,
): void {
  if (checkIsServiceMode()) {
    handleServiceAccessRequiredResponse(httpStatus, body);
    return;
  }
  const ssoProviderIds = readSessionExpiredSsoProviderIds(httpStatus, body);
  if (ssoProviderIds) {
    void restartExpiredSessionSignIn(ssoProviderIds);
  }
}

/**
 * `fetch` that starts {@link restartExpiredSessionSignIn} on a session-expired
 * response, for clients outside axios and RTK Query. The response is returned
 * unread.
 */
export const fetchWithSessionExpiry: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  if (response.status === 401) {
    void readJsonBody(response.clone()).then((body) => {
      handleSessionExpiredResponse(response.status, body);
    });
  }
  return response;
};

async function readJsonBody(response: Response): Promise<unknown> {
  try {
    const body: unknown = await response.json();
    return body;
  } catch {
    return null;
  }
}
